/**
 * PersonalizeStep
 *
 * Data enrichment step - redesigned with world-class editorial aesthetics.
 * Inspired by Collins, Pentagram design studios.
 *
 * Design Philosophy:
 * - Clean, focused form design
 * - Subtle animations that don't distract
 * - Refined input interactions
 * - Clear visual hierarchy
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { cn } from '../../../lib/cn';
import {
  Building2,
  Phone,
  MessageCircle,
  ChevronRight,
  ChevronDown,
  Shield,
} from 'lucide-react';
import {
  OnboardingLayout,
  OnboardingStepContainer,
  OnboardingHeading,
  OnboardingButton,
} from '../OnboardingLayout';
import type { OnboardingStepProps } from '../OnboardingRouter';

// Attribution sources
const ATTRIBUTION_OPTIONS = [
  { value: 'google', label: 'Google Search' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'colleague', label: 'Colleague or Friend' },
  { value: 'advertisement', label: 'Advertisement' },
  { value: 'real_estate_group', label: 'Real Estate Group' },
  { value: 'other', label: 'Other' },
];

// Stagger animation variants
const formVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.2,
    },
  },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export function PersonalizeStep({ onStepComplete, onboardingData }: OnboardingStepProps) {
  const { user, profile, refreshProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    company_name: profile?.company_name || '',
    phone: profile?.phone || '',
    how_heard: '',
  });

  // Determine if user is a pro (show company field)
  const isPro =
    profile?.user_segment === 'agent' ||
    profile?.user_segment === 'photographer' ||
    profile?.user_segment === 'brokerage';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);

    try {
      // Update profile with enrichment data
      const updates: Record<string, string> = {
        updated_at: new Date().toISOString(),
      };

      if (formData.company_name.trim()) {
        updates.company_name = formData.company_name.trim();
      }

      if (formData.phone.trim()) {
        updates.phone = formData.phone.trim();
      }

      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      // Refresh profile
      await refreshProfile();

      // Track analytics
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'onboarding_personalize_completed',
          has_company: !!formData.company_name.trim(),
          has_phone: !!formData.phone.trim(),
          how_heard: formData.how_heard || 'not_provided',
          session_id: onboardingData.session_id,
        });
      }

      // Complete step
      await onStepComplete('personalize', {
        how_heard_about_us: formData.how_heard || undefined,
      });
    } catch (error) {
      console.error('Error saving personalization:', error);
      // Still proceed even on error
      await onStepComplete('personalize');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    // Track skip
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'onboarding_personalize_skipped',
        session_id: onboardingData.session_id,
      });
    }

    await onStepComplete('personalize', {
      skipped_personalization: true,
    });
  };

  return (
    <OnboardingLayout showProgress={true} showSkip={true}>
      <OnboardingStepContainer maxWidth="lg">
        <OnboardingHeading
          eyebrow="Final touches"
          title="Almost there"
          subtitle="Help us personalize your experience (optional)"
        />

        <motion.form
          onSubmit={handleSubmit}
          variants={formVariants}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {/* Company name (for pros) */}
          {isPro && (
            <motion.div variants={fieldVariants}>
              <label
                htmlFor="company_name"
                className={cn(
                  'flex items-center gap-2 text-sm font-sans font-medium mb-3 transition-colors duration-200',
                  focusedField === 'company_name' ? 'text-charcoal' : 'text-sand'
                )}
              >
                <Building2 className="size-4" />
                Company or Brokerage
              </label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                onFocus={() => setFocusedField('company_name')}
                onBlur={() => setFocusedField(null)}
                placeholder="e.g., RE/MAX Premier"
                className={cn(
                  'w-full px-5 py-4 rounded-2xl border bg-white text-charcoal font-sans',
                  'placeholder:text-stone transition-all duration-200',
                  'focus:outline-none focus:ring-0',
                  focusedField === 'company_name'
                    ? 'border-olive shadow-soft'
                    : 'border-stone/30 hover:border-stone/50'
                )}
              />
            </motion.div>
          )}

          {/* Phone number */}
          <motion.div variants={fieldVariants}>
            <label
              htmlFor="phone"
              className={cn(
                'flex items-center gap-2 text-sm font-sans font-medium mb-3 transition-colors duration-200',
                focusedField === 'phone' ? 'text-charcoal' : 'text-sand'
              )}
            >
              <Phone className="size-4" />
              Phone Number
              <span className="font-normal text-stone ml-1">(optional)</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onFocus={() => setFocusedField('phone')}
              onBlur={() => setFocusedField(null)}
              placeholder="(555) 123-4567"
              className={cn(
                'w-full px-5 py-4 rounded-2xl border bg-white text-charcoal font-sans',
                'placeholder:text-stone transition-all duration-200',
                'focus:outline-none focus:ring-0',
                focusedField === 'phone'
                  ? 'border-olive shadow-soft'
                  : 'border-stone/30 hover:border-stone/50'
              )}
            />
            <p className="mt-2 text-xs text-stone font-sans flex items-center gap-1.5">
              <Shield className="size-3" />
              For account recovery only
            </p>
          </motion.div>

          {/* How did you hear about us */}
          <motion.div variants={fieldVariants}>
            <label
              htmlFor="how_heard"
              className={cn(
                'flex items-center gap-2 text-sm font-sans font-medium mb-3 transition-colors duration-200',
                focusedField === 'how_heard' ? 'text-charcoal' : 'text-sand'
              )}
            >
              <MessageCircle className="size-4" />
              How did you find us?
            </label>
            <div className="relative">
              <select
                id="how_heard"
                name="how_heard"
                value={formData.how_heard}
                onChange={handleChange}
                onFocus={() => setFocusedField('how_heard')}
                onBlur={() => setFocusedField(null)}
                className={cn(
                  'w-full px-5 py-4 rounded-2xl border bg-white text-charcoal font-sans',
                  'appearance-none cursor-pointer transition-all duration-200',
                  'focus:outline-none focus:ring-0',
                  focusedField === 'how_heard'
                    ? 'border-olive shadow-soft'
                    : 'border-stone/30 hover:border-stone/50',
                  !formData.how_heard && 'text-stone'
                )}
              >
                <option value="">Select an option</option>
                {ATTRIBUTION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 size-5 text-sand pointer-events-none" />
            </div>
          </motion.div>

          {/* Submit button */}
          <motion.div variants={fieldVariants} className="pt-4">
            <OnboardingButton
              type="submit"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Continue
              <ChevronRight className="size-5" />
            </OnboardingButton>
          </motion.div>

          {/* Skip link */}
          <motion.div variants={fieldVariants} className="text-center">
            <button
              type="button"
              onClick={handleSkip}
              disabled={isSubmitting}
              className="text-sand hover:text-charcoal text-sm font-sans transition-colors duration-200"
            >
              Skip for now
            </button>
          </motion.div>
        </motion.form>

        {/* Privacy note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 border border-stone/20 rounded-full">
            <Shield className="size-4 text-olive" />
            <p className="text-xs text-sand font-sans">
              Your information is private and secure
            </p>
          </div>
        </motion.div>
      </OnboardingStepContainer>
    </OnboardingLayout>
  );
}
